// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import loadAddressFields from "@insite/client-framework/Store/Data/AddressFields/Handlers/LoadAddressFields";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { AddressFieldDisplayCollectionModel, CustomerValidationDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CustomerAddressFormStyles } from "@insite/content-library/Components/CustomerAddressForm";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import { CreateNewAddressModalStyles } from "@insite/content-library/Widgets/CheckoutShipping/CreateNewAddressModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TaxFields from "@waytek/Components/TaxFields";
import { ToastBody, ToastStyle } from "@waytek/MobiusOverrides/Toast/Toast";
import CustomerAddressForm, { AddressModel } from "@waytek/Overrides/Components/CreateAccountCustomerAddressForm";
import addAccount from "@waytek/Store/Data/Accounts/Handlers/AddAccount";
import { CustomState } from "@waytek/Store/Reducers";
import { TaxCertificateType } from "@waytek/Store/State/TaxFields/TaxFieldsState";
import {
    AvalaraAddressModel,
    AvalaraMessageModel,
    AvalaraValidationResponse,
} from "@waytek/Types/AvalaraValidationModel";
import { colorStrings, mediaQueriesBP } from "@waytek/Utilities/variables";
import AvalaraValidationComponentModal from "@waytek/Widgets/Custom/AvalaraValidationComponentModal";
import parse from "html-react-parser";
import React, { ReactNode, RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    text = "text",
    title = "title",
}

const mapDispatchToProps = {
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    loadAddressFields,
};

const mapStateToProps = (state: CustomState) => {
    const billToAddressFields = getAddressFieldsDataView(state).value?.billToAddressFields;
    const shipToAddressFields = getAddressFieldsDataView(state).value?.shipToAddressFields;

    let billToAddressFieldsEdited: AddressFieldDisplayCollectionModel | undefined;
    if (billToAddressFields != null) {
        billToAddressFieldsEdited = {
            ...billToAddressFields,
            email: {
                displayName: "",
                uri: "",
                properties: {},
                isVisible: false,
            },
        };
    }

    let shipToAddressFieldsEdited: AddressFieldDisplayCollectionModel | undefined;
    if (shipToAddressFields != null) {
        shipToAddressFieldsEdited = {
            ...shipToAddressFields,
            email: {
                displayName: "",
                uri: "",
                properties: {},
                isVisible: false,
            },
        };
    }

    const { accountSettings, customSettings } = getSettingsCollection(state);
    const guestErpNumber = (customSettings as any).guestErpCustomerErpNumber
        ? (customSettings as any).guestErpCustomerErpNumber
        : null;

    return {
        accountSettings,
        returnUrl: getCreateAccountReturnUrl(state),
        countries: getCurrentCountries(state),
        shipToAddressFields: shipToAddressFieldsEdited as Readonly<AddressFieldDisplayCollectionModel> | undefined,
        billToAddressFields: billToAddressFieldsEdited as Readonly<AddressFieldDisplayCollectionModel> | undefined,
        guestErpNumber,
        // Verndale Customization: WYTKXOP-2495 : Add Tax Fields to Create Address Form
        taxID: state.taxFields.taxID,
        taxCertificate: state.taxFields.taxCertificate as TaxCertificateType,
    };
};

interface OwnProps extends WidgetProps {
    extendedStyles?: CreateNewAddressModalStyles;
    fields: {
        [fields.text]: string;
        [fields.title]: string;
    };
}

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userInfoGridWrapper?: GridItemProps;
    userInfoGridItem?: GridItemProps;
    userInfoGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    createAccountBenefits?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    userGridContainerTitleWrapper?: InjectableCss;
    userGridContainerTitleText?: TypographyProps;
    fieldContainer?: GridItemProps;
    userInformationBlockContainer?: GridContainerProps;
    userBillingAndShippingInfo?: GridItemProps;
    userBillingInfo?: GridItemProps;
    userShippingInfo?: GridItemProps;
    createAccountTextGridItem?: GridItemProps;
    createAccountBenefitsContainer?: GridContainerProps;
    createAccountTextGridTitle?: TypographyProps;
    createAccountTextGridText?: TypographyProps;
    addressForm?: CustomerAddressFormStyles;
    billingAddressMessageGridItem?: GridItemProps;
    billingAddressMessage?: TypographyProps;
    billingAddressFormGridItem?: GridItemProps;
    useBillingAddressMessage?: TypographyProps;
    useBillingAddressMessageGridItem?: GridItemProps;
    createAccountMessagesGridItem?: GridItemProps;
    createAccountMessagesGridItemMobile?: GridItemProps;
    messageWrapper?: InjectableCss;
    messageWrapperMobile?: GridItemProps;
    toastMobile?: InjectableCss;
    // XNG-Change
    taxGridItem?: GridItemProps;
}

export const createAccountStyles: CreateAccountStyles = {
    mainGridContainer: {
        css: css`
            &&& {
                padding-top: 24px;

                & > div {
                    width: 100%;
                    margin: 0;
                }

                ${mediaQueriesBP.maxMD} {
                    padding-top: 14px;

                    & > div {
                        width: 100%;
                        margin: 0;
                    }
                }
            }
        `,
    },

    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },

    userInfoGridWrapper: {
        width: [12, 12, 6, 8, 8],
    },

    userInfoGridItem: {
        width: 12,
    },

    emailGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            padding: 0;
        `,
    },

    passwordGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            padding: 0;
        `,
    },

    userInformationBlockContainer: {
        gap: 0,
        css: css`
            padding: 0;
            ${mediaQueriesBP.minLG} {
                padding: 0 55px 0 0;
            }
        `,
    },

    fieldContainer: {
        width: 12,
        css: css`
            &&& {
                margin-bottom: 20px;
            }
        `,
    },

    userBillingAndShippingInfo: {
        width: 12,
        css: css`
            &&& {
                margin-top: 40px;
            }
        `,
    },

    userBillingInfo: {
        width: [12, 12, 12, 6, 6],
        css: css`
            ${mediaQueriesBP.minLG} {
                padding-right: 40px;
            }
        `,
    },

    userShippingInfo: {
        width: [12, 12, 12, 6, 6],
        css: css`
            ${mediaQueriesBP.minLG} {
                padding-left: 20px;
            }
        `,
    },

    userGridContainerTitleWrapper: {
        css: css`
            width: 100%;
            border-bottom: 3px solid ${getColor(colorStrings.commonGlobalIndustrialTaupe)};
            margin-bottom: 20px;
            padding-bottom: 4px;
        `,
    },

    userGridContainerTitleText: {
        as: "h2",
        variant: "h6",
        css: css`
            font-size: 18px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.78;
            letter-spacing: normal;
            margin: 0;
        `,
    },

    billingAddressMessageGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },

    billingAddressMessage: {
        size: "14px",
        lineHeight: "24px",
    },

    billingAddressFormGridItem: {
        width: 12,
        css: css`
            &&& {
                padding: 0;
                margin-top: 15px;
            }
        `,
    },

    useBillingAddressMessageGridItem: {
        width: 12,
        css: css`
            &&& {
                padding: 0;
                align-items: center;
                margin-bottom: 7px;
            }
        `,
    },

    useBillingAddressMessage: {
        size: "12px",
        lineHeight: "24px",
        css: css`
            margin-left: 15px;
        `,
    },

    addressForm: {
        formFields: {
            container: {
                gap: 20,
            },

            firstNameGridItem: {
                width: [12, 12, 12, 6, 6],
            },

            lastNameGridItem: {
                width: [12, 12, 12, 6, 6],
            },

            companyNameGridItem: {
                width: 12,
            },

            stateGridItem: {
                width: [12, 12, 12, 6, 6],
            },

            postalCodeGridItem: {
                width: [12, 12, 12, 6, 6],
            },

            phoneGridItem: {
                width: 12,
            },
        },

        submitButton: {
            css: css`
                &&& {
                    display: none;
                }
            `,
        },
    },

    subscriptionGridItem: {
        width: 12,
        css: css`
            &&& {
                padding: 0;
                ${mediaQueriesBP.maxLG} {
                    margin-bottom: 10px;
                }
            }
        `,
    },

    submitErrorTitle: {
        color: "danger",
    },

    reCaptchaGridItem: { width: 12 },

    submitGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
            padding: 0;
            flex-wrap: wrap;
        `,
    },

    submitButton: {
        css: css`
            width: 100%;
        `,
    },

    submitErrorGridItem: {
        width: 12,
        css: css`
            &&& {
                margin-top: 5px;
                margin-bottom: -20px;
                span {
                    font-size: 14px;
                }
            }
        `,
    },

    createAccountBenefits: {
        width: [12, 12, 6, 4, 4],
        css: css`
            padding: 0;
            ${mediaQueriesBP.minMD} {
                flex-direction: column;
                padding: 36px 0 0 40px;
            }
        `,
    },

    createAccountBenefitsContainer: {
        css: css`
            justify-content: flex-end;
        `,
    },

    createAccountTextGridItem: {
        width: 12,
        css: css`
            &&& {
                background-color: ${getColor(colorStrings.commonGlobalFadedTaupe)};
                border: 1px solid ${getColor(colorStrings.commonGlobalIndustrialTaupe)};
                padding: 20px 20px 25px;
                margin-top: 30px;
                flex-direction: column;
            }
        `,
    },

    createAccountTextGridTitle: {
        as: "h3",
        variant: "h6",
        css: css`
            font-size: 18px;
            font-weight: 500;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.33;
            letter-spacing: normal;
        `,
    },

    createAccountTextGridText: {
        css: css`
            line-height: 1.7;
            ol,
            ul {
                padding-left: 15px;
            }
        `,
    },

    passwordRequirementsGridContainer: {
        gap: 5,
    },

    passwordRequirementsGridItem: {
        width: 12,
    },

    requirementsTitle: {
        variant: "h4",
        as: "h2",
    },

    createAccountMessagesGridItem: { width: 12 },
    createAccountMessagesGridItemMobile: {
        width: 12,
        css: css`
            &&& {
                padding: 0;
                margin-bottom: 20px;
            }
        `,
    },
    messageWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            margin-top: 10px;
            padding: 0;
            width: 100%;
            max-width: 100%;
            box-shadow: none;

            & > div {
                box-shadow: none;

                ${mediaQueriesBP.minLG} {
                    width: 100%;
                }

                ${mediaQueriesBP.maxLG} {
                    width: 100%;
                    max-width: 100%;
                    margin-top: 40px;
                    margin-left: 0;
                }

                ${mediaQueriesBP.maxMD} {
                    display: none;
                }
            }
        `,
    },
    messageWrapperMobile: {
        width: 12,
        css: css`
            &&& {
                margin-top: 10px;
                ${mediaQueriesBP.minMD} {
                    display: none;
                }
            }
        `,
    },

    toastMobile: {
        css: css`
            width: 100%;
            box-shadow: none;
        `,
    },
};

const styles = createAccountStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const StyledForm = getStyledWrapper("form");

const defaultAddress: AddressModel = {
    isNew: true,
    address1: "",
    address2: "",
    address3: "",
    address4: "",
    attention: "",
    budgetEnforcementLevel: "",
    city: "",
    companyName: "",
    contactFullName: "",
    costCodeTitle: "",
    country: null,
    customerCurrencySymbol: "",
    customerName: "",
    customerNumber: "",
    customerSequence: "",
    email: "",
    fax: "",
    firstName: "",
    fullAddress: "",
    id: "",
    isDefault: true,
    isGuest: false,
    label: "",
    lastName: "",
    phone: "",
    postalCode: "",
    shipTosUri: "",
    state: null,
    uri: "",
    properties: {},
};

const parseFieldToValidation = (fieldsInfo: AddressFieldDisplayCollectionModel): CustomerValidationDto => ({
    address1: {
        isDisabled: false,
        isRequired: fieldsInfo.address1?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.address1?.properties["maxLength"] ? +fieldsInfo.address1?.properties["maxLength"] : null,
    },
    address2: {
        isDisabled: false,
        isRequired: fieldsInfo.address2?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.address2?.properties["maxLength"] ? +fieldsInfo.address2?.properties["maxLength"] : null,
    },
    address3: {
        isDisabled: false,
        isRequired: fieldsInfo.address3?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.address3?.properties["maxLength"] ? +fieldsInfo.address3?.properties["maxLength"] : null,
    },
    address4: {
        isDisabled: false,
        isRequired: fieldsInfo.address4?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.address4?.properties["maxLength"] ? +fieldsInfo.address4?.properties["maxLength"] : null,
    },
    attention: {
        isDisabled: false,
        isRequired: fieldsInfo.attention?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.attention?.properties["maxLength"]
            ? +fieldsInfo.attention?.properties["maxLength"]
            : null,
    },
    city: {
        isDisabled: false,
        isRequired: fieldsInfo.city?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.city?.properties["maxLength"] ? +fieldsInfo.city?.properties["maxLength"] : null,
    },
    companyName: {
        isDisabled: false,
        isRequired: fieldsInfo.companyName?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.companyName?.properties["maxLength"]
            ? +fieldsInfo.companyName?.properties["maxLength"]
            : null,
    },
    country: {
        isDisabled: false,
        isRequired: fieldsInfo.country?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.country?.properties["maxLength"] ? +fieldsInfo.country?.properties["maxLength"] : null,
    },
    email: {
        isDisabled: true,
        isRequired: false,
        maxLength: null,
    },
    fax: {
        isDisabled: false,
        isRequired: fieldsInfo.fax?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.fax?.properties["maxLength"] ? +fieldsInfo.fax?.properties["maxLength"] : null,
    },
    firstName: {
        isDisabled: false,
        isRequired: fieldsInfo.firstName?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.firstName?.properties["maxLength"]
            ? +fieldsInfo.firstName?.properties["maxLength"]
            : null,
    },
    lastName: {
        isDisabled: false,
        isRequired: fieldsInfo.lastName?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.lastName?.properties["maxLength"] ? +fieldsInfo.lastName?.properties["maxLength"] : null,
    },
    phone: {
        isDisabled: false,
        isRequired: fieldsInfo.phone?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.phone?.properties["maxLength"] ? +fieldsInfo.phone?.properties["maxLength"] : null,
    },
    postalCode: {
        isDisabled: false,
        isRequired: fieldsInfo.postalCode?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.postalCode?.properties["maxLength"]
            ? +fieldsInfo.postalCode?.properties["maxLength"]
            : null,
    },
    state: {
        isDisabled: false,
        isRequired: fieldsInfo.state?.properties["isRequired"] === "true",
        maxLength: fieldsInfo.state?.properties["maxLength"] ? +fieldsInfo.state?.properties["maxLength"] : null,
    },
});

const CreateAccount = ({
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha,
    accountSettings,
    returnUrl,
    fields,
    countries,
    shipToAddressFields,
    billToAddressFields,
    loadAddressFields,
    guestErpNumber,
    // Verndale Customization: WYTKXOP-2495 : Add Tax Fields to Create Address Form
    taxID,
    taxCertificate,
}: // eslint-disable-next-line
// eslint-disable-next-line sonarjs/cognitive-complexity
Props) => {
    const userNameRequiredFieldMessage = siteMessage("CreateNewAccountInfo_UserName_Required");
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");

    const [email, setEmail] = useState("");
    const [userName, setUserName] = useState("");
    const [userNameError, setUserNameError] = useState(
        accountSettings.useEmailAsUserName ? "" : userNameRequiredFieldMessage,
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [password, setPassword] = useState("");
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<ReactNode>("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState<ReactNode>("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    /* Verndale Custom Feature - START */
    const confirmEmailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const confirmEmailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
    const confirmEmailMatchingMessage = siteMessage("CreateNewAccountInfo_ConfirmEmailAddress_Matching");
    const [confirmEmail, setConfirmEmail] = useState("");
    const [confirmEmailError, setConfirmEmailError] = useState(confirmEmailRequiredFieldMessage);
    const [useBillingAddress, setUseBillingAddress] = useState(true);
    const [newAddress, setNewAddress] = useState<AddressModel>(defaultAddress);
    const [newShipToAddress, setNewShipToAddress] = useState<AddressModel>(defaultAddress);
    const [showOutsideMessage, setShowOutsideMessage] = useState(true);
    const [verifyNewAddress, setVerifyNewAddress] = useState(true);
    const [verifyNewShipToAddress, setVerifyNewShipToAddress] = useState(true);
    const [forceValidation, setForceValidation] = useState(false);
    const [shouldExecuteSubmit, setShouldExecuteSubmit] = useState(false);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [enteredBtAddress, setEnteredBtAddress] = useState<AvalaraAddressModel>({});
    const [enteredStAddress, setEnteredStAddress] = useState<AvalaraAddressModel>({});
    const [validationAddresses, setValidationAddresses] = useState<AvalaraValidationResponse>({});
    const [addressProperties, setAddressProperties] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        loadAddressFields({});
    }, []);

    useEffect(() => {
        if (!newAddress.validation && billToAddressFields) {
            setNewAddress({
                ...newAddress,
                validation: parseFieldToValidation(billToAddressFields),
            });
        }

        if (newAddress != null) {
            validateAddressMessage();
        }
    }, [newAddress, billToAddressFields]);

    useEffect(() => {
        if (!newShipToAddress.validation && shipToAddressFields) {
            setNewShipToAddress({
                ...newShipToAddress,
                validation: parseFieldToValidation(shipToAddressFields),
            });
        }

        if (newShipToAddress != null) {
            validateAddressMessage();
        }
    }, [newShipToAddress, shipToAddressFields]);

    useEffect(() => {
        if (
            shouldExecuteSubmit &&
            ((useBillingAddress && verifyNewAddress) ||
                (!useBillingAddress && verifyNewAddress && verifyNewShipToAddress))
        ) {
            triggerFinalSubmit();
        }
    }, [shouldExecuteSubmit, useBillingAddress, verifyNewAddress, verifyNewShipToAddress]);
    /* Verndale Custom Feature - END */

    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
        useEmailAsUserName,
    } = accountSettings;

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        if (confirmEmail !== email) {
            setConfirmEmailError(confirmEmailMatchingMessage);
        } else if (confirmEmail === email && confirmEmailError === confirmEmailMatchingMessage) {
            setConfirmEmailError("");
        }
        return !errorMessage;
    };

    const validateConfirmEmail = (confirmEmail: string) => {
        const errorMessage = !confirmEmail
            ? confirmEmailRequiredFieldMessage
            : confirmEmail !== email
            ? confirmEmailMatchingMessage
            : emailRegexp.test(confirmEmail)
            ? ""
            : confirmEmailFieldMessage;
        setConfirmEmailError(errorMessage);
        return !errorMessage;
    };

    const validateUserName = (name: string) => {
        const errorMessage = !name && !useEmailAsUserName ? userNameRequiredFieldMessage : "";
        setUserNameError(errorMessage);
        return !errorMessage;
    };

    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        const errorMessage =
            newPassword !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch")
                : "";
        setConfirmPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(emailError || confirmEmailError || passwordErrorMessage || confirmPasswordErrorMessage);
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const confirmEmailChangeHandler = (confirmEmail: string) => {
        validateConfirmEmail(confirmEmail);
        setConfirmEmail(confirmEmail);
    };

    const userChangeHandler = (name: string) => {
        validateUserName(name);
        setUserName(name);
    };

    const passwordChangeHandler = (newPassword: string) => {
        validateConfirmPassword(newPassword, confirmPassword);
        validatePassword({
            password: newPassword,
            onComplete: errorMessage => {
                setPasswordErrorMessage(errorMessage);
            },
        });
        setPassword(newPassword);
    };

    const confirmPasswordChangeHandler = (newConfirmPassword: string) => {
        setConfirmPassword(newConfirmPassword);
        validateConfirmPassword(password, newConfirmPassword);
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Account created successfully!"), messageType: "success" });
        setTimeout(() => {
            signIn({
                userName: email,
                password,
                rememberMe: false,
                returnUrl,
            });
        }, 500);
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitted(true);
        const addressEvent = new CustomEvent("custom_trigger_address_form_submit");
        const addresses: NodeList = (event.target as any).querySelectorAll(".custom__create-account-address");
        setVerifyNewAddress(false);
        setVerifyNewShipToAddress(false);
        let counter = 0;
        addresses.forEach(el => {
            counter += 15;
            setTimeout(() => {
                el.dispatchEvent(addressEvent);
            }, counter);
        });
        setShouldExecuteSubmit(true);
    };

    const handleUseBillingAddressCheck = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        if (!value) {
            setNewShipToAddress({
                ...defaultAddress,
                validation: parseFieldToValidation(shipToAddressFields!),
            });
        }
        setVerifyNewAddress(false);
        setVerifyNewShipToAddress(false);
        setUseBillingAddress(value);
    };

    const handleAddressSubmit = (address: AddressModel, isBilling: boolean, ev: any) => {
        if (isBilling) {
            setNewAddress(address);
            if (ev != null) {
                setVerifyNewAddress(true);
            }
        } else {
            setNewShipToAddress(address);
            if (ev != null) {
                setVerifyNewShipToAddress(true);
            }
        }
    };

    // eslint-disable-next-line
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const validateAddressMessage = () => {
        const addresses = [newAddress];
        if (!useBillingAddress) {
            addresses.push(newShipToAddress);
        }

        let condition = false;
        for (const address of addresses) {
            if (address.country !== null && address.country.properties["isOutsideUs"]?.toLowerCase() === "true") {
                condition = true;
                break;
                // eslint-disable-next-line
            } else if (address.state != null && address.state.properties["isOutsideUs"]?.toLowerCase() === "true") {
                condition = true;
                break;
            }
        }
        setShowOutsideMessage(condition);
        return condition;
    };

    // eslint-disable-next-line
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const triggerFinalSubmit = async () => {
        if (!validateSubmitEnabled()) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        const properties: { [key: string]: string } = {
            btAddress1: newAddress.address1,
            btAddress2: newAddress.address2,
            btAddress3: newAddress.address3,
            btAddress4: newAddress.address4,
            btAttention: newAddress.attention,
            btCity: newAddress.city,
            btCompanyName: newAddress.companyName,
            btCountry: newAddress.country?.id || "",
            btFax: newAddress.fax,
            btFirstName: newAddress.firstName,
            btLastName: newAddress.lastName,
            btPhone: newAddress.phone,
            btPostalCode: newAddress.postalCode,
            btState: newAddress.state?.id || "",
            btEmail: email,
            stEmail: email,
        };
        const shippingAddress = useBillingAddress ? { ...newAddress } : { ...newShipToAddress };
        properties.stAddress1 = shippingAddress.address1;
        properties.stAddress2 = shippingAddress.address2;
        properties.stAddress3 = shippingAddress.address3;
        properties.stAddress4 = shippingAddress.address4;
        properties.stAttention = shippingAddress.attention;
        properties.stCity = shippingAddress.city;
        properties.stCompanyName = shippingAddress.companyName;
        properties.stCountry = shippingAddress.country?.id || "";
        properties.stFax = shippingAddress.fax;
        properties.stFirstName = shippingAddress.firstName;
        properties.stLastName = shippingAddress.lastName;
        properties.stPhone = shippingAddress.phone;
        properties.stPostalCode = shippingAddress.postalCode;
        properties.stState = shippingAddress.state?.id || "";
        properties.guestErpNumber = guestErpNumber;
        properties.isSecondSubmission = "false";

        // Verndale Customization: WYTKXOP-2495 : Add Tax Fields to Create Address Form
        properties.taxID = taxID ?? "";
        properties.taxCertificate = taxCertificate ? taxCertificate.base64 : "";
        properties.taxCertificateExpirationDate = "";
        // End Verndale Customization

        setAddressProperties(properties);
        setEnteredBtAddress({
            line1: properties.btAddress1,
            line2: properties.btAddress2,
            line3: properties.btAddress3,
            city: properties.btCity,
            country: newAddress.country?.name,
            postalCode: properties.btPostalCode,
            region: newAddress.state?.abbreviation,
        });
        setEnteredStAddress({
            line1: properties.stAddress1,
            line2: properties.stAddress2,
            line3: properties.stAddress3,
            city: properties.stCity,
            country: shippingAddress.country?.name,
            postalCode: properties.stPostalCode,
            region: shippingAddress.state?.abbreviation,
        });
        handleFinalSubmit(properties);
    };

    // eslint-disable-next-line
    /* eslint-disable sonarjs/cognitive-complexity */
    const handleFinalSubmit = (properties: { [key: string]: string }, isModal = false) => {
        setShowValidationModal(false);
        setValidationAddresses({});
        setErrorMessage("");
        setIsSubmitting(true);
        addAccount({
            email,
            userName: email,
            password,
            isSubscribed,
            properties,
            onSuccess: onSuccessHandler,
            onError: (
                error: string,
                response?: { errorMessage?: string; avalaraValidation?: AvalaraValidationResponse },
            ) => {
                if (response?.avalaraValidation) {
                    setValidationAddresses(response.avalaraValidation);
                    setShowValidationModal(true);
                    if (!isModal) {
                        const messages: AvalaraMessageModel[] = [];
                        if (
                            response.avalaraValidation.btValidateAddressResponse?.messages &&
                            response.avalaraValidation.btValidateAddressResponse?.messages.length > 0
                        ) {
                            // eslint-disable-next-line
                            messages.push(...response.avalaraValidation.btValidateAddressResponse?.messages);
                        }
                        if (
                            response.avalaraValidation.stValidateAddressResponse?.messages &&
                            response.avalaraValidation.stValidateAddressResponse?.messages.length > 0
                        ) {
                            // eslint-disable-next-line
                            messages.push(...response.avalaraValidation.stValidateAddressResponse?.messages);
                        }
                        for (const message of messages) {
                            toasterContext.addToast({ body: <span>{message.details}</span>, messageType: "danger" });
                        }
                    }
                } else {
                    setErrorMessage(error);
                }
                setIsSubmitting(false);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                } else {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.("");
                }
            },
        });
    };
    // eslint-disable-next-line
    /* eslint-enable sonarjs/cognitive-complexity */

    const isSubmitEnabled = validateSubmitEnabled();

    const onAddressChangeHandler = () => {
        setForceValidation(false);
    };

    const handleCloseValidationModal = () => {
        setShowValidationModal(false);
        setForceValidation(false);
    };

    const handleSubmitValidationModal = (btAddress?: AvalaraAddressModel, stAddress?: AvalaraAddressModel) => {
        let newProperties: { [key: string]: string } = {};
        if (btAddress && stAddress) {
            newProperties = {
                ...addressProperties,
                btAddress1: btAddress.line1!,
                btCity: btAddress.city!,
                btCountry: btAddress.country!,
                btPostalCode: btAddress.postalCode!,
                btState: btAddress.region!,
                btEmail: email,
                stAddress1: stAddress.line1!,
                stCity: stAddress.city!,
                stCountry: stAddress.country!,
                stPostalCode: stAddress.postalCode!,
                stState: stAddress.region!,
                stEmail: email,
            };

            if (btAddress.line2) {
                newProperties.btAddress2 = btAddress.line2;
            }
            if (btAddress.line3) {
                newProperties.btAddress3 = btAddress.line3;
            }
            delete newProperties.btAddress4;

            if (stAddress.line2) {
                newProperties.stAddress2 = stAddress.line2;
            }
            if (stAddress.line3) {
                newProperties.stAddress3 = stAddress.line3;
            }
            delete newProperties.stAddress4;

            newProperties.guestErpNumber = guestErpNumber;
        } else {
            newProperties = { ...addressProperties };
        }
        newProperties.isSecondSubmission = "true";
        handleFinalSubmit(newProperties, true);
    };

    return (
        <>
            <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                <GridContainer {...styles.mainGridContainer}>
                    <GridItem {...styles.userInfoGridWrapper}>
                        <GridContainer {...styles.userInfoGridContainer}>
                            <GridItem {...styles.userInfoGridItem}>
                                <GridContainer>
                                    <StyledWrapper {...styles.userGridContainerTitleWrapper}>
                                        <Typography {...styles.userGridContainerTitleText}>
                                            {translate("User Information")}
                                        </Typography>
                                    </StyledWrapper>
                                    <GridItem {...styles.emailGridItem}>
                                        <GridContainer {...styles.userInformationBlockContainer}>
                                            <GridItem {...styles.fieldContainer}>
                                                <TextField
                                                    {...styles.emailTextField}
                                                    type="email"
                                                    label={translate("Email (This will be your User ID)")}
                                                    onChange={e => emailChangeHandler(e.currentTarget.value)}
                                                    value={email}
                                                    data-test-selector="createAccount_email"
                                                    required
                                                    error={isSubmitted && emailError}
                                                />
                                            </GridItem>
                                            <GridItem {...styles.fieldContainer}>
                                                <TextField
                                                    {...styles.emailTextField}
                                                    type="email"
                                                    label={translate("Re-enter Email ")}
                                                    onChange={e => confirmEmailChangeHandler(e.currentTarget.value)}
                                                    value={confirmEmail}
                                                    data-test-selector="createAccount_confirmEmail"
                                                    required
                                                    error={isSubmitted && confirmEmailError}
                                                />
                                            </GridItem>
                                            <GridItem {...styles.subscriptionGridItem}>
                                                <CheckboxGroup>
                                                    <Checkbox
                                                        onChange={(e, value) => setIsSubscribed(value)}
                                                        checked={isSubscribed}
                                                        data-test-selector="createAccount_Subscription"
                                                    >
                                                        {siteMessage("SignIn_Sign_Up_Newsletter")}
                                                    </Checkbox>
                                                </CheckboxGroup>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                    <GridItem {...styles.passwordGridItem}>
                                        <GridContainer {...styles.userInformationBlockContainer}>
                                            <GridItem {...styles.fieldContainer}>
                                                <TextField
                                                    id="password"
                                                    {...styles.passwordTextField}
                                                    type={showPassword ? undefined : "password"}
                                                    label={translate("Password")}
                                                    value={password}
                                                    onChange={e => passwordChangeHandler(e.currentTarget.value)}
                                                    error={isSubmitted && passwordErrorMessage}
                                                    iconProps={{
                                                        ...styles.passwordIcon,
                                                        src: showPassword ? EyeOff : Eye,
                                                    }}
                                                    iconClickableProps={{
                                                        onClick: () => {
                                                            setShowPassword(!showPassword);
                                                        },
                                                        type: "button",
                                                    }}
                                                    clickableText={password}
                                                    data-test-selector="createAccount_password"
                                                    required
                                                />
                                            </GridItem>
                                            <GridItem {...styles.fieldContainer}>
                                                <TextField
                                                    id="confirm-password"
                                                    {...styles.confirmPasswordTextField}
                                                    type={showConfirmPassword ? undefined : "password"}
                                                    label={translate("Re-enter Password")}
                                                    value={confirmPassword}
                                                    onChange={e => confirmPasswordChangeHandler(e.currentTarget.value)}
                                                    error={isSubmitted && confirmPasswordErrorMessage}
                                                    iconProps={{
                                                        ...styles.confirmPasswordIcon,
                                                        src: showConfirmPassword ? EyeOff : Eye,
                                                    }}
                                                    iconClickableProps={{
                                                        onClick: () => {
                                                            setShowConfirmPassword(!showConfirmPassword);
                                                        },
                                                        type: "button",
                                                    }}
                                                    clickableText={confirmPassword}
                                                    data-test-selector="createAccount_confirmPassword"
                                                    required
                                                />
                                            </GridItem>
                                            <GridItem {...styles.messageWrapperMobile}>
                                                <StyledWrapper>
                                                    <ToastStyle
                                                        transitionState="entered"
                                                        transitionLength={0}
                                                        messageType="warning"
                                                        data-test-selector="CreateAccount_OutsideMessage"
                                                        {...styles.toastMobile}
                                                    >
                                                        <ToastBody data-id="toast-body">
                                                            <Typography>
                                                                {siteMessage("CheckoutShipping_OutsideUS")}
                                                            </Typography>
                                                        </ToastBody>
                                                    </ToastStyle>
                                                </StyledWrapper>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                    <GridItem {...styles.reCaptchaGridItem}>
                                        <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.userBillingAndShippingInfo}>
                                <GridContainer>
                                    <GridItem {...styles.userBillingInfo}>
                                        <GridContainer>
                                            <StyledWrapper {...styles.userGridContainerTitleWrapper}>
                                                <Typography {...styles.userGridContainerTitleText}>
                                                    {translate("Billing Address")}
                                                </Typography>
                                            </StyledWrapper>
                                            <GridItem {...styles.billingAddressMessageGridItem}>
                                                <Typography {...styles.billingAddressMessage}>
                                                    {siteMessage("CreateNewAccountInfo_BillingAddressMessage")}
                                                </Typography>
                                            </GridItem>
                                            <GridItem
                                                {...styles.billingAddressFormGridItem}
                                                data-test-selector="create-account-bill-to"
                                            >
                                                {countries && billToAddressFields && (
                                                    <CustomerAddressForm
                                                        address={newAddress}
                                                        countries={countries}
                                                        addressFieldDisplayCollection={billToAddressFields}
                                                        onSubmit={(ev, address) =>
                                                            handleAddressSubmit(address, true, ev)
                                                        }
                                                        extendedStyles={styles.addressForm}
                                                        handleFieldsChange={onAddressChangeHandler}
                                                        // Verndale Customization: WYTKXOP-2495
                                                        hasTaxFields={false}
                                                    />
                                                )}
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                    <GridItem {...styles.userShippingInfo}>
                                        <GridContainer>
                                            <StyledWrapper {...styles.userGridContainerTitleWrapper}>
                                                <Typography {...styles.userGridContainerTitleText}>
                                                    {translate("Shipping Address")}
                                                </Typography>
                                            </StyledWrapper>
                                            <GridItem {...styles.useBillingAddressMessageGridItem}>
                                                <CheckboxGroup>
                                                    <Checkbox
                                                        onChange={handleUseBillingAddressCheck}
                                                        checked={useBillingAddress}
                                                        data-test-selector="createAccount_Subscription"
                                                    >
                                                        {translate("Use Billing Address")}
                                                    </Checkbox>
                                                </CheckboxGroup>
                                                <Typography {...styles.useBillingAddressMessage}>
                                                    {siteMessage("CreateAccount_UseBillingAddress")}
                                                </Typography>
                                            </GridItem>
                                            {!useBillingAddress && countries && shipToAddressFields && (
                                                <GridItem
                                                    {...styles.billingAddressFormGridItem}
                                                    data-test-selector="create-account-ship-to"
                                                >
                                                    <CustomerAddressForm
                                                        address={newShipToAddress}
                                                        countries={countries}
                                                        addressFieldDisplayCollection={shipToAddressFields}
                                                        onSubmit={(ev, address) =>
                                                            handleAddressSubmit(address, false, ev)
                                                        }
                                                        extendedStyles={styles.addressForm}
                                                        handleFieldsChange={onAddressChangeHandler}
                                                        // Verndale Customization: WYTKXOP-2495
                                                        hasTaxFields={true}
                                                    />
                                                </GridItem>
                                            )}
                                            {useBillingAddress && countries &&
                                                <GridItem
                                                    {...styles.taxGridItem}
                                                    data-test-selector="createaccount-taxGridItem"
                                                >
                                                    <TaxFields state={newAddress.state?.id} />
                                                </GridItem>
                                            }
                                            {showOutsideMessage && (
                                                <GridItem {...styles.createAccountMessagesGridItemMobile}>
                                                    <StyledWrapper css={styles.messageWrapperMobile?.css}>
                                                        <ToastStyle
                                                            transitionState="entered"
                                                            transitionLength={0}
                                                            messageType="warning"
                                                            data-test-selector="CreateAccount_NotAvailableStateMessage"
                                                            {...styles.toastMobile}
                                                        >
                                                            <ToastBody data-id="toast-body">
                                                                <Typography>
                                                                    {siteMessage("CheckoutShipping_NotAvailableState")}
                                                                </Typography>
                                                            </ToastBody>
                                                        </ToastStyle>
                                                    </StyledWrapper>
                                                </GridItem>
                                            )}
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.createAccountBenefits}>
                        <GridContainer gap={0} {...styles.createAccountBenefitsContainer}>
                            <GridItem {...styles.submitGridItem}>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                                    data-test-selector="createAccount_createButton"
                                >
                                    {translate("Register Now")}
                                </Button>
                            </GridItem>

                            {errorMessage && (
                                <GridItem
                                    data-test-selector="createAccount_ErrorMessage"
                                    {...styles.submitErrorGridItem}
                                >
                                    <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                                </GridItem>
                            )}

                            {fields.title && fields.text && (
                                <GridItem {...styles.createAccountTextGridItem}>
                                    {fields.title && (
                                        <Typography {...styles.createAccountTextGridTitle}>
                                            {parse(fields.title, parserOptions)}
                                        </Typography>
                                    )}

                                    {fields.text && (
                                        <Typography {...styles.createAccountTextGridText}>
                                            {parse(fields.text, parserOptions)}
                                        </Typography>
                                    )}
                                </GridItem>
                            )}

                            <GridItem {...styles.createAccountMessagesGridItem}>
                                <StyledWrapper {...styles.messageWrapper}>
                                    <ToastStyle
                                        transitionState="entered"
                                        transitionLength={0}
                                        messageType="warning"
                                        data-test-selector="CreateAccount_OutsideMessage"
                                    >
                                        <ToastBody data-id="toast-body">
                                            <Typography>{siteMessage("CheckoutShipping_OutsideUS")}</Typography>
                                        </ToastBody>
                                    </ToastStyle>
                                </StyledWrapper>
                            </GridItem>

                            {showOutsideMessage && (
                                <GridItem {...styles.createAccountMessagesGridItem}>
                                    <StyledWrapper {...styles.messageWrapper}>
                                        <ToastStyle
                                            transitionState="entered"
                                            transitionLength={0}
                                            messageType="warning"
                                            data-test-selector="CreateAccount_NotAvailableStateMessage"
                                        >
                                            <ToastBody data-id="toast-body">
                                                <Typography>
                                                    {siteMessage("CheckoutShipping_NotAvailableState")}
                                                </Typography>
                                            </ToastBody>
                                        </ToastStyle>
                                    </StyledWrapper>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </StyledForm>

            <AvalaraValidationComponentModal
                key="avalara-create-account"
                showModal={showValidationModal}
                handleModalClose={handleCloseValidationModal}
                handleModalSubmit={handleSubmitValidationModal}
                enteredBtAddress={enteredBtAddress}
                enteredStAddress={enteredStAddress}
                addresses={validationAddresses}
                useBillingAddress={useBillingAddress}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CreateAccount) as any,
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Sidebar Title",
                editorTemplate: "TextField",
                defaultValue: "Create Account Benefits",
                fieldType: "Translatable",
            },
            {
                name: fields.text,
                displayName: "Sidebar Text",
                editorTemplate: "RichTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
